/**
 * Shared MCP tool registry.
 * Defines the full tool tree: categories, names, labels, and result type shapes.
 * Used by backend (MCP server, LangGraph callbacks) and frontend (ToolDisplays).
 */

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export enum McpToolCategory {
  Navigation = "Navigation",
  Search = "Search",
  Data = "Data",
  KnowledgeBase = "KnowledgeBase",
  Mutation = "Mutation",
}

export enum McpToolName {
  // Navigation
  NavigateToInvestment = "navigateToInvestment",
  NavigateToCompany = "navigateToCompany",
  NavigateToFirm = "navigateToFirm",
  NavigateToKnowledgeBase = "navigateToKnowledgeBase",
  // Search
  SearchEntities = "searchEntities",
  SearchInvestments = "searchInvestments",
  SearchFirms = "searchFirms",
  SearchCompanies = "searchCompanies",
  // Data
  GetPerformanceAnalytics = "getPerformanceAnalytics",
  GetQuarterlyReturns = "getQuarterlyReturns",
  GetInvestmentDetails = "getInvestmentDetails",
  GetPerformanceStatistics = "getPerformanceStatistics",
  GetPmeOverview = "getPmeOverview",
  GetPmePositions = "getPmePositions",
  GetPmeCashflows = "getPmeCashflows",
  // Knowledge Base
  SavedKnowledgeBase = "savedKnowledgeBase",
  GetPublishedFields = "getPublishedFields",
  SearchFieldKeys = "searchFieldKeys",
  // Mutation (agentic data creation and updates)
  DiscoverMutations = "discoverMutations",
  DescribeMutation = "describeMutation",
  DryRunMutation = "dryRunMutation",
  StageMutationPlan = "stageMutationPlan",
  GetGroups = "getGroups",
  // Reporting
  GenerateDiligenceReport = "generateDiligenceReport",
}

// ---------------------------------------------------------------------------
// Tool tree: category → tool names
// ---------------------------------------------------------------------------

export const MCP_TOOL_TREE: Record<McpToolCategory, McpToolName[]> = {
  [McpToolCategory.Navigation]: [
    McpToolName.NavigateToInvestment,
    McpToolName.NavigateToCompany,
    McpToolName.NavigateToFirm,
    McpToolName.NavigateToKnowledgeBase,
  ],
  [McpToolCategory.Search]: [
    McpToolName.SearchEntities,
    McpToolName.SearchInvestments,
    McpToolName.SearchFirms,
    McpToolName.SearchCompanies,
  ],
  [McpToolCategory.Data]: [
    McpToolName.GetPerformanceAnalytics,
    McpToolName.GetQuarterlyReturns,
    McpToolName.GetInvestmentDetails,
    McpToolName.GetPerformanceStatistics,
    McpToolName.GetPmeOverview,
    McpToolName.GetPmePositions,
    McpToolName.GetPmeCashflows,
  ],
  [McpToolCategory.KnowledgeBase]: [
    McpToolName.SavedKnowledgeBase,
    McpToolName.GetPublishedFields,
    McpToolName.SearchFieldKeys,
  ],
  [McpToolCategory.Mutation]: [
    McpToolName.DiscoverMutations,
    McpToolName.DescribeMutation,
    McpToolName.DryRunMutation,
    McpToolName.GetGroups,
    McpToolName.StageMutationPlan,
  ],
};

// ---------------------------------------------------------------------------
// Reverse lookup: tool name → category
// ---------------------------------------------------------------------------

export const MCP_TOOL_CATEGORY = Object.entries(MCP_TOOL_TREE).reduce(
  (acc, [category, tools]) => {
    for (const tool of tools) {
      acc[tool] = category as McpToolCategory;
    }
    return acc;
  },
  {} as Record<McpToolName, McpToolCategory>
);

// ---------------------------------------------------------------------------
// Human-readable display labels
// ---------------------------------------------------------------------------

export const MCP_TOOL_LABELS: Record<McpToolName, string> = {
  [McpToolName.NavigateToInvestment]: "Link to Investment",
  [McpToolName.NavigateToCompany]: "Link to Company",
  [McpToolName.NavigateToFirm]: "Link to Firm",
  [McpToolName.NavigateToKnowledgeBase]: "Link to Knowledge Base",
  [McpToolName.SearchEntities]: "Search Entities",
  [McpToolName.SearchInvestments]: "Search Investments",
  [McpToolName.SearchFirms]: "Search Firms",
  [McpToolName.SearchCompanies]: "Search Companies",
  [McpToolName.GetPerformanceAnalytics]: "Performance Analytics",
  [McpToolName.GetQuarterlyReturns]: "Quarterly Returns",
  [McpToolName.GetInvestmentDetails]: "Investment Details",
  [McpToolName.GetPerformanceStatistics]: "Performance Statistics",
  [McpToolName.GetPmeOverview]: "PME Summary",
  [McpToolName.GetPmePositions]: "PME Positions",
  [McpToolName.GetPmeCashflows]: "PME Cashflows",
  [McpToolName.SavedKnowledgeBase]: "Knowledge Base",
  [McpToolName.GetPublishedFields]: "Published Fields",
  [McpToolName.SearchFieldKeys]: "Search Field Keys",
  [McpToolName.DiscoverMutations]: "Discover Data Updates",
  [McpToolName.DescribeMutation]: "Describe Data Update",
  [McpToolName.DryRunMutation]: "Dry Run Data Update",
  [McpToolName.StageMutationPlan]: "Stage Data Updates",
  [McpToolName.GetGroups]: "Get Groups",
  [McpToolName.GenerateDiligenceReport]: "Diligence Report",
};

// ---------------------------------------------------------------------------
// Result type shapes (for frontend renderers and backend type-safety)
// ---------------------------------------------------------------------------

export interface NavigationLink {
  url: string;
  label: string;
  description?: string;
  type?: "section" | "page" | "knowledgeBase";
}

export interface NavigationToolResult {
  url: string;
  action: "link";
  title: string;
  tab?: string;
}

export interface SearchToolResult {
  count: number;
  results: Array<{
    id: number;
    name: string;
    type?: string;
    firm?: { id: number; name: string };
  }>;
  byType?: Record<string, number>;
}

export interface DataToolNavigation {
  primary: NavigationLink;
  knowledgeBase?: NavigationLink;
  related: NavigationLink[];
  formatted: string;
}

export interface DataToolPagination {
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface DataToolResult {
  investmentId: number;
  investmentName: string;
  summary: string;
  navigation: DataToolNavigation;
  pagination?: DataToolPagination;
}

export interface KnowledgeBaseToolResult {
  id: number;
  name: string;
  status: string;
  documentCount: number;
  noteCount: number;
}

export interface SearchFieldKeysToolResult {
  count: number;
  summary: string;
  fieldKeys: Array<{
    fieldKey: string;
    questionText: string;
    category?: string;
    section?: string;
    displayName?: string;
  }>;
}

export interface PublishedFieldsToolResult {
  count: number;
  hasMore: boolean;
  knowledgeBaseName?: string;
  fields: Array<{
    fieldKey: string;
    value: unknown;
    ownerType: string;
    ownerId: number;
    ownerName: string | null;
    modifyDate: string | null;
  }>;
}
